import { ReactElement, ReactNode, useEffect } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import produce from 'immer'
import cloneDeep from 'lodash-es/cloneDeep'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getPlanImageCards } from '@queries'
import type { PlanImageCard } from 'src/domain/planImageCards/types'
import { usePersistentParams } from 'src/hooks'
import { planImageCardDtoToPlanImageCard } from './utils'

interface ScenePointer {
  x: number
  y: number
  z: number
}

interface PlanImageCardState {
  isVisible: boolean
  cards: PlanImageCard[]
  placeholderCardIds: string[]
  imageContentVersions: Record<string, number>
  /** Loaded image aspect ratio (width/height) per card, reported by the scene preview. */
  imageAspectRatios: Record<string, number>
  lastScenePointer: ScenePointer | null
}

const initialState: PlanImageCardState = {
  isVisible: true,
  cards: [],
  placeholderCardIds: [],
  imageContentVersions: {},
  imageAspectRatios: {},
  lastScenePointer: null,
}

const usePlanImageCardStore = create(
  combine(cloneDeep(initialState), set => ({
    clear: () => set(cloneDeep(initialState)),

    setIsVisible: (isVisible: boolean) => set({ isVisible }),

    setCards: (cards: PlanImageCard[]) =>
      set(
        produce((state: PlanImageCardState) => {
          state.cards = cards
          state.placeholderCardIds = state.placeholderCardIds.filter(id =>
            cards.some(card => card.id === id),
          )
        }),
      ),

    updateCardAttributes: (cardId: string, attributes: Partial<PlanImageCard>) =>
      set(
        produce((state: PlanImageCardState) => {
          const index = state.cards.findIndex(card => card.id === cardId)
          if (index === -1) return
          state.cards[index] = { ...state.cards[index], ...attributes }
        }),
      ),

    markPlaceholderCard: (cardId: string) =>
      set(
        produce((state: PlanImageCardState) => {
          if (!state.placeholderCardIds.includes(cardId)) {
            state.placeholderCardIds.push(cardId)
          }
        }),
      ),

    clearPlaceholderCard: (cardId: string) =>
      set(
        produce((state: PlanImageCardState) => {
          state.placeholderCardIds = state.placeholderCardIds.filter(id => id !== cardId)
        }),
      ),

    bumpImageContentVersion: (cardId: string) =>
      set(
        produce((state: PlanImageCardState) => {
          state.imageContentVersions[cardId] = (state.imageContentVersions[cardId] ?? 0) + 1
        }),
      ),

    setImageAspectRatio: (cardId: string, aspectRatio: number) =>
      set(
        produce((state: PlanImageCardState) => {
          state.imageAspectRatios[cardId] = aspectRatio
        }),
      ),

    setLastScenePointer: (pointer: ScenePointer | null) => set({ lastScenePointer: pointer }),
  })),
)

interface PlanImageCardStoreProviderProps {
  children: ReactNode
}

const PlanImageCardStoreProvider = ({
  children,
}: PlanImageCardStoreProviderProps): ReactElement => {
  const clear = usePlanImageCardStore(state => state.clear)
  const setCards = usePlanImageCardStore(state => state.setCards)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const { enqueueSnackbar } = useSnackbar()

  useQuery({
    queryKey: getPlanImageCards.getKey(projectId),
    queryFn: () => getPlanImageCards.request(projectId),
    enabled: !!projectId,
    onSuccess: data => {
      setCards(data.map(planImageCardDtoToPlanImageCard))
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Plan-Bilder'), {
        variant: 'error',
      })
      setCards([])
    },
  })

  useEffect(() => {
    return () => {
      clear()
    }
  }, [clear])

  return <>{children}</>
}

export { PlanImageCardStoreProvider, usePlanImageCardStore }
